import { PageFactory, APIOutput, ILegalContact, LegalContact, ServerSidePaginatedLoadingOutput } from "@amzn/ask-legal-domain";
import { Button, Flashbar, SpaceBetween } from "@amzn/awsui-components-react";
import * as React from "react";
import { useServerSideCollection } from "../../hooks/legal-contacts-collection-hook";
import { AppContext } from "../../setup/context";
import { useAPI2 } from "../../hooks/api-hook";
import { LegalContactsPolarisFactory } from "../../factory/polaris/legal-contacts-polaris-factory";
import { ServerSideLegalContactsTable } from "./ServerSideLegalContactsTable";
import { LegalContactFactory } from "../../factory/legal-contact-factory";
import { LegalContactConstants } from "../../utils/legal-contact.constant";

export const AllLegalContactsTable = (props: {
    instanceId: string;
}) => {
    const context = React.useContext(AppContext);
    const loadAllLegalContactsApiRunner = useAPI2(
        context.getLegalContactAPI().loadAll
    );
    const [exporting, setExporting] = React.useState(false);
    const [exportError, setExportError] = React.useState<string | undefined>();
    const collection = useServerSideCollection(
        loadAllLegalContactsApiRunner,
        props.instanceId,
        {
            columnDefinitions: LegalContactsPolarisFactory.Table.toAdminColumnDefinition(),
            defaultSortingColumn: LegalContactsPolarisFactory.DEFAULT_SORTING_STATE.sortingColumn,
            filteringProperties: LegalContactsPolarisFactory.Filters.PropertyFilters.toDefaultFilteringProperties(),
            selectionType: "single"
        }
    );
    const getContainerURLWithFilter = (containerId: string, legalContactUserId: string) => {
        if (!containerId) return "";
        return `#/page/${
            PageFactory.fromEntityId(containerId)
        }/live?container=${
            containerId
        }&filter=contact: ${
            legalContactUserId
        }`;
    };

    const handleExport = async () => {
        setExporting(true);
        setExportError(undefined);
        try {
            let allContacts: LegalContact.Record[] = [];
            let pageIndex = 1;
            let totalCount = Infinity;
            while (allContacts.length < totalCount && pageIndex <= LegalContactConstants.EXPORT_MAX_PAGES) {
                const response = await context.getLegalContactAPI().loadAll(
                    ILegalContact.LoadAllLegalContactsInput.create({
                        instanceId: props.instanceId,
                        pagination: { currentPageIndex: pageIndex, pageSize: LegalContactConstants.EXPORT_PAGE_SIZE },
                        sorting: { field: "legalContactUser.name", order: "ASC" }
                    })
                );
                const output = APIOutput.fromRaw<ServerSidePaginatedLoadingOutput<LegalContact.Record>>(response.data);
                if (!output.isOk()) throw new Error(output.err.message);
                totalCount = output.data.totalCount;
                allContacts = allContacts.concat(output.data.result);
                pageIndex++;
            }
            LegalContactFactory.exportToFile({
                items: allContacts,
                filePrefix: "All_Legal_Contacts",
                getPageLink: (contact) => contact.containerId && contact.legalContactUser?.id
                    ? window.location.origin + getContainerURLWithFilter(contact.containerId, contact.legalContactUser.id)
                    : ""
            });
        } catch (e) {
            setExportError(e instanceof Error ? e.message : "Export failed. Please try again.");
        } finally {
            setExporting(false);
        }
    };

    const viewOnPageButton = <Button
        disabled={collection.tableProps.props.selectedItems.length < 1}
        iconName="external"
        href={getContainerURLWithFilter(
            collection.tableProps.props.selectedItems[0]?.containerId,
            collection.tableProps.props.selectedItems[0]?.legalContactUser.id
        )}
        target="_blank"
    >
        View on Page
    </Button>;

    const headerActions = <SpaceBetween size="s" direction="horizontal">
        {viewOnPageButton}
        <Button iconName="download" loading={exporting} onClick={handleExport}>Export</Button>
    </SpaceBetween>;

    return <SpaceBetween size="s">
        {exportError && <Flashbar items={[{ type: "error", content: exportError, dismissible: true, onDismiss: () => setExportError(undefined) }]} />}
        <ServerSideLegalContactsTable collection={collection} headerActions={headerActions} />
    </SpaceBetween>;
};